import { gapiConfig } from '@/config/gapi-config';
import { useStore } from '@/store';
import { OAuth2Client } from 'google-auth-library';
import { useAuth } from './auth';

export const gapiOnLoad = async () =>
  Promise.all([
    new Promise<void>(resolve => gapi.load('picker', resolve)),
    new Promise<void>(resolve => gapi.load('client', resolve))
  ]);

export const initGapi = async () => {
  gapi.client.load('drive', 'v3');
  gapi.client.load('docs', 'v1');
};

export const useGapi = () => {
  const { drive } = gapi.client;
  const { docs } = gapi.client;
  const { signIn } = useAuth();
  const store = useStore();

  const verifyAccessToken = async () =>
    new Promise<void>(resolve =>
      (async () => {
        const { accessToken, refreshToken } = store.state.auth;

        const testClient = new OAuth2Client({
          clientId: process.env.VUE_APP_GAPI_CLIENT_ID,
          clientSecret: process.env.VUE_APP_GAPI_CLIENT_SECRET
        });
        testClient.setCredentials({ access_token: accessToken, refresh_token: refreshToken });

        try {
          const info = await testClient.getTokenInfo(accessToken as string);
          if (info.expiry_date && info.expiry_date < Date.now()) {
            throw new Error('Token expired');
          }
          resolve();
        } catch (error) {
          const { credentials } = await testClient.refreshAccessToken();
          if (credentials.access_token && credentials.access_token !== accessToken) {
            await store.dispatch.auth.setAccessToken(credentials.access_token);
            resolve();
          }
        }
      })()
    );

  const signInToGoogle = async () =>
    new Promise(resolve => {
      gapiConfig.callback = async (gapiResponse: { code: string }) => resolve(await signIn(gapiResponse));

      const authClient = google.accounts.oauth2.initCodeClient(gapiConfig);
      authClient.requestCode();
    });

  const initPicker = (viewId: google.picker.ViewId) => async (callback: (id: string) => void) => {
    console.log('Initializing Picker with viewId:', viewId);

    await verifyAccessToken();
    console.log('Access token verified.');

    const { accessToken } = store.state.auth;
    console.log('Retrieved access token from store:', accessToken);

    const innerCallback = (data: google.picker.ResponseObject): void => {
      console.log('Picker callback triggered with data:', data);

      if (data.action !== google.picker.Action.PICKED) {
        console.log('No item picked, action was:', data.action);
        return;
      }

      const pickedId = data.docs[0].id;
      console.log('Item picked with ID:', pickedId);
      callback(pickedId);
    };

    const docsView = new google.picker.DocsView()
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes('application/vnd.google-apps.folder');
    console.log('DocsView created:', docsView);

    if (accessToken && process.env.VUE_APP_GAPI_API_KEY && process.env.VUE_APP_GAPI_PROJECT_NUMBER) {
      console.log('Building Picker with API key, project number, and access token.');
      const picker = new google.picker.PickerBuilder()
        .addView(viewId === google.picker.ViewId.DOCUMENTS ? viewId : docsView)
        .setAppId(process.env.VUE_APP_GAPI_PROJECT_NUMBER)
        .setOAuthToken(accessToken)
        // .setDeveloperKey(process.env.VUE_APP_GAPI_API_KEY)
        .setCallback(innerCallback)
        .build();
      console.log('Picker built:', picker);

      picker.setVisible(true);
      console.log('Picker set to visible.');
    } else {
      console.warn('Missing access token, API key, or project number. Picker not initialized.');
    }
  };

  return {
    drive,
    docs,
    verifyAccessToken,
    signInToGoogle,
    initPicker
  };
};
